<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { AlertCircle, CirclePause, CirclePlay, RefreshCw, Search, ServerCog } from "lucide-vue-next";
import { api } from "../services/api";
import type { SystemLogCategory, SystemLogEntry, SystemLogLevel, SystemLogResult } from "../types";

const result = ref<SystemLogResult>();
const loading = ref(false);
const error = ref("");
const autoRefresh = ref(true);
const category = ref<"" | SystemLogCategory>("");
const level = ref<"" | SystemLogLevel>("");
const search = ref("");
let refreshTimer: number | undefined;
let searchTimer: number | undefined;

const errorCount = computed(() => result.value?.items.filter((item) => item.level === "error" || item.level === "fatal").length ?? 0);
const mqttCount = computed(() => result.value?.items.filter((item) => item.category === "mqtt").length ?? 0);
const realtimeCount = computed(() => result.value?.items.filter((item) => item.category === "realtime").length ?? 0);

function formatTime(time: number): string {
  return new Date(time).toLocaleString("zh-CN", { hour12: false });
}

function eventLabel(item: SystemLogEntry): string {
  if (item.event) return item.event;
  const request = item.data.req as { method?: string; url?: string } | undefined;
  return request ? `${request.method ?? "HTTP"} ${request.url ?? ""}` : item.category;
}

function detailJson(item: SystemLogEntry): string {
  return JSON.stringify(item.data, null, 2);
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = "";
  try {
    result.value = await api.systemLogs({
      take: 300,
      category: category.value || undefined,
      level: level.value || undefined,
      search: search.value,
    });
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "日志加载失败";
  } finally {
    loading.value = false;
  }
}

function updateTimer(): void {
  if (refreshTimer) window.clearInterval(refreshTimer);
  refreshTimer = undefined;
  if (autoRefresh.value) refreshTimer = window.setInterval(() => void load(), 3_000);
}

watch([category, level], () => void load());
watch(search, () => {
  if (searchTimer) window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => void load(), 350);
});
watch(autoRefresh, updateTimer);

onMounted(() => {
  void load();
  updateTimer();
});
onBeforeUnmount(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
  if (searchTimer) window.clearTimeout(searchTimer);
});
</script>

<template>
  <div class="view-stack system-log-page">
    <section class="log-summary-band">
      <div><span class="eyebrow">LIVE DIAGNOSTICS</span><h2>系统运行日志</h2><p>查看 MQTT、实时语音、AI 调用和接口请求的最近运行记录。</p></div>
      <div class="log-summary-metrics">
        <div><strong>{{ result?.retained ?? 0 }}</strong><span>内存保留</span></div>
        <div><strong>{{ mqttCount }}</strong><span>MQTT</span></div>
        <div><strong>{{ realtimeCount }}</strong><span>实时语音</span></div>
        <div :class="{ danger: errorCount > 0 }"><strong>{{ errorCount }}</strong><span>错误</span></div>
      </div>
    </section>

    <section class="surface-section log-console-section">
      <div class="log-toolbar">
        <div class="log-filter-group">
          <label><span>分类</span><select v-model="category"><option value="">全部</option><option value="mqtt">MQTT</option><option value="realtime">实时语音</option><option value="ai">AI / 知识库</option><option value="http">HTTP</option><option value="system">系统</option></select></label>
          <label><span>级别</span><select v-model="level"><option value="">全部</option><option value="info">信息</option><option value="warn">警告</option><option value="error">错误</option><option value="debug">调试</option></select></label>
          <label class="log-search"><span>搜索</span><div><Search :size="15" /><input v-model="search" placeholder="命令号、追踪号、事件或消息" /></div></label>
        </div>
        <div class="log-toolbar-actions">
          <button class="button secondary" type="button" @click="autoRefresh = !autoRefresh"><CirclePause v-if="autoRefresh" :size="16" /><CirclePlay v-else :size="16" />{{ autoRefresh ? "暂停刷新" : "继续刷新" }}</button>
          <button class="icon-button" type="button" title="立即刷新" :disabled="loading" @click="load"><RefreshCw :size="18" :class="{ spinning: loading }" /></button>
        </div>
      </div>

      <div v-if="error" class="log-error"><AlertCircle :size="16" />{{ error }}</div>
      <div class="log-table-header"><span>时间</span><span>级别</span><span>分类</span><span>事件与内容</span></div>
      <div v-if="result?.items.length" class="system-log-list">
        <details v-for="item in result.items" :key="item.id" class="system-log-row">
          <summary>
            <time>{{ formatTime(item.time) }}</time>
            <span :class="['log-level', item.level]">{{ item.level.toUpperCase() }}</span>
            <span :class="['log-category', item.category]">{{ item.category }}</span>
            <span class="log-message"><strong>{{ eventLabel(item) }}</strong><small>{{ item.message }}<template v-if="item.traceId"> · {{ item.traceId }}</template></small></span>
          </summary>
          <pre>{{ detailJson(item) }}</pre>
        </details>
      </div>
      <div v-else-if="!loading" class="empty-state compact-empty"><ServerCog :size="28" /><span>当前筛选条件下暂无日志</span></div>
      <footer class="log-console-footer"><span>匹配 {{ result?.available ?? 0 }} 条，显示最近 {{ result?.items.length ?? 0 }} 条</span><span v-if="result">采集始于 {{ new Date(result.capturedSince).toLocaleString("zh-CN") }}</span></footer>
    </section>
  </div>
</template>
